const DefaultDataPrivacy = ({
  lang,
  companyName,
  address,
  privacyOfficer,
}: {
  lang: string;
  companyName: string;
  address: React.ReactNode;
  privacyOfficer: React.ReactNode;
}) => {
  if (lang === "de") {
    return (
      <div className="min-h-full px-5 text-foreground gap-4 flex flex-col">
        <h1 className="font-semibold text-foreground/90 text-2xl leading-[28px] tracking-[-0.015em] text-center mb-6">
          Datenschutzbestimmungen
        </h1>

        <p>
          Wir freuen uns sehr über Ihr Interesse an unserem Unternehmen.
          Datenschutz hat einen besonders hohen Stellenwert für die{" "}
          {companyName} (in weiterer Folge „datenschutzrechtlich
          Verantwortlicher“ genannt). Eine Nutzung der Internetseiten des
          datenschutzrechtlich Verantwortlichen ist grundsätzlich ohne jede
          Angabe personenbezogener Daten möglich. Sofern eine betroffene Person
          besondere Services unseres Unternehmens über unsere Internetseite in
          Anspruch nehmen möchte, könnte jedoch eine Verarbeitung
          personenbezogener Daten erforderlich werden. Ist die Verarbeitung
          personenbezogener Daten erforderlich und besteht für eine solche
          Verarbeitung keine gesetzliche Grundlage, holen wir generell eine
          Einwilligung der betroffenen Person ein.
        </p>
        <p>
          Die Verarbeitung personenbezogener Daten, beispielsweise des Namens,
          der Anschrift, E-Mail-Adresse oder Telefonnummer einer betroffenen
          Person, erfolgt stets im Einklang mit der Datenschutz-Grundverordnung
          und in Übereinstimmung mit den für den datenschutzrechtlich
          Verantwortlichen geltenden landesspezifischen Datenschutzbestimmungen.
          Mittels dieser Datenschutzerklärung möchte unser Unternehmen die
          Öffentlichkeit über Art, Umfang und Zweck der von uns erhobenen,
          genutzten und verarbeiteten personenbezogenen Daten informieren.
          Ferner werden betroffene Personen mittels dieser Datenschutzerklärung
          über die ihnen zustehenden Rechte aufgeklärt.
        </p>
        <p>
          Der datenschutzrechtlich Verantwortliche hat als für die Verarbeitung
          Verantwortlicher zahlreiche technische und organisatorische Maßnahmen
          umgesetzt, um einen möglichst lückenlosen Schutz der über diese
          Internetseite verarbeiteten personenbezogenen Daten sicherzustellen.
          Dennoch können Internetbasierte Datenübertragungen grundsätzlich
          Sicherheitslücken aufweisen, sodass ein absoluter Schutz nicht
          gewährleistet werden kann.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          1. Begriffsbestimmungen
        </h2>
        <p>
          Die Datenschutzerklärung der {companyName} beruht auf den
          Begrifflichkeiten, die durch den Europäischen Richtlinien- und
          Verordnungsgeber beim Erlass der Datenschutz-Grundverordnung (DSGVO)
          verwendet wurden. Unsere Datenschutzerklärung soll sowohl für die
          Öffentlichkeit als auch für unsere Kunden und Geschäftspartner einfach
          lesbar und verständlich sein. Um dies zu gewährleisten, möchten wir
          vorab die verwendeten Begrifflichkeiten erläutern.
        </p>
        <p>
          Wir verwenden in dieser Datenschutzerklärung unter anderem die
          folgenden Begriffe:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <strong>personenbezogene Daten:</strong> Personenbezogene Daten sind
            alle Informationen, die sich auf eine identifizierte oder
            identifizierbare natürliche Person (im Folgenden „betroffene
            Person“) beziehen. Als identifizierbar wird eine natürliche Person
            angesehen, die direkt oder indirekt, insbesondere mittels Zuordnung
            zu einer Kennung wie einem Namen, zu einer Kennnummer, zu
            Standortdaten, zu einer Online-Kennung oder zu einem oder mehreren
            besonderen Merkmalen, die Ausdruck der physischen, physiologischen,
            genetischen, psychischen, wirtschaftlichen, kulturellen oder
            sozialen Identität dieser natürlichen Person sind, identifiziert
            werden kann.
          </li>
          <li>
            <strong>betroffene Person:</strong> Betroffene Person ist jede
            identifizierte oder identifizierbare natürliche Person, deren
            personenbezogene Daten von dem für die Verarbeitung Verantwortlichen
            verarbeitet werden.
          </li>
          <li>
            <strong>Verarbeitung:</strong> Verarbeitung ist jeder mit oder ohne
            Hilfe automatisierter Verfahren ausgeführte Vorgang oder jede solche
            Vorgangsreihe im Zusammenhang mit personenbezogenen Daten wie das
            Erheben, das Erfassen, die Organisation, das Ordnen, die
            Speicherung, die Anpassung oder Veränderung, das Auslesen, das
            Abfragen, die Verwendung, die Offenlegung durch Übermittlung,
            Verbreitung oder eine andere Form der Bereitstellung, den Abgleich
            oder die Verknüpfung, die Einschränkung, das Löschen oder die
            Vernichtung.
          </li>
          <li>
            <strong>Einschränkung der Verarbeitung:</strong> Einschränkung der
            Verarbeitung ist die Markierung gespeicherter personenbezogener
            Daten mit dem Ziel, ihre künftige Verarbeitung einzuschränken.
          </li>
          <li>
            <strong>Profiling:</strong> Profiling ist jede Art der
            automatisierten Verarbeitung personenbezogener Daten, die darin
            besteht, dass diese personenbezogenen Daten verwendet werden, um
            bestimmte persönliche Aspekte, die sich auf eine natürliche Person
            beziehen, zu bewerten, insbesondere, um Aspekte bezüglich
            Arbeitsleistung, wirtschaftlicher Lage, Gesundheit, persönlicher
            Vorlieben, Interessen, Zuverlässigkeit, Verhalten, Aufenthaltsort
            oder Ortswechsel dieser natürlichen Person zu analysieren oder
            vorherzusagen.
          </li>
          <li>
            <strong>Pseudonymisierung:</strong> Pseudonymisierung ist die
            Verarbeitung personenbezogener Daten in einer Weise, auf welche die
            personenbezogenen Daten ohne Hinzuziehung zusätzlicher Informationen
            nicht mehr einer spezifischen betroffenen Person zugeordnet werden
            können, sofern diese zusätzlichen Informationen gesondert aufbewahrt
            werden und technischen und organisatorischen Maßnahmen unterliegen,
            die gewährleisten, dass die personenbezogenen Daten nicht einer
            identifizierten oder identifizierbaren natürlichen Person zugewiesen
            werden.
          </li>
          <li>
            <strong>
              Verantwortlicher oder für die Verarbeitung Verantwortlicher:
            </strong>{" "}
            Verantwortlicher oder für die Verarbeitung Verantwortlicher ist die
            natürliche oder juristische Person, Behörde, Einrichtung oder andere
            Stelle, die allein oder gemeinsam mit anderen über die Zwecke und
            Mittel der Verarbeitung von personenbezogenen Daten entscheidet.
            Sind die Zwecke und Mittel dieser Verarbeitung durch das Unionsrecht
            oder das Recht der Mitgliedstaaten vorgegeben, so kann der
            Verantwortliche beziehungsweise können die bestimmten Kriterien
            seiner Benennung nach dem Unionsrecht oder dem Recht der
            Mitgliedstaaten vorgesehen werden.
          </li>
          <li>
            <strong>Auftragsverarbeiter:</strong> Auftragsverarbeiter ist eine
            natürliche oder juristische Person, Behörde, Einrichtung oder andere
            Stelle, die personenbezogene Daten im Auftrag des Verantwortlichen
            verarbeitet.
          </li>
          <li>
            <strong>Empfänger:</strong> Empfänger ist eine natürliche oder
            juristische Person, Behörde, Einrichtung oder andere Stelle, der
            personenbezogene Daten offengelegt werden, unabhängig davon, ob es
            sich bei ihr um einen Dritten handelt oder nicht. Behörden, die im
            Rahmen eines bestimmten Untersuchungsauftrags nach dem Unionsrecht
            oder dem Recht der Mitgliedstaaten möglicherweise personenbezogene
            Daten erhalten, gelten jedoch nicht als Empfänger.
          </li>
          <li>
            <strong>Dritter:</strong> Dritter ist eine natürliche oder
            juristische Person, Behörde, Einrichtung oder andere Stelle außer
            der betroffenen Person, dem Verantwortlichen, dem
            Auftragsverarbeiter und den Personen, die unter der unmittelbaren
            Verantwortung des Verantwortlichen oder des Auftragsverarbeiters
            befugt sind, die personenbezogenen Daten zu verarbeiten.
          </li>
          <li>
            <strong>Einwilligung:</strong> Einwilligung ist jede von der
            betroffenen Person freiwillig für den bestimmten Fall in
            informierter Weise und unmissverständlich abgegebene
            Willensbekundung in Form einer Erklärung oder einer sonstigen
            eindeutigen bestätigenden Handlung, mit der die betroffene Person zu
            verstehen gibt, dass sie mit der Verarbeitung der sie betreffenden
            personenbezogenen Daten einverstanden ist.
          </li>
        </ul>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          2. Name und Anschrift des für die Verarbeitung Verantwortlichen
        </h2>
        <p>
          Verantwortlicher im Sinne der Datenschutz-Grundverordnung, sonstiger
          in den Mitgliedstaaten der Europäischen Union geltenden
          Datenschutzgesetze und anderer Bestimmungen mit datenschutzrechtlichem
          Charakter ist die:
        </p>
        <address className="not-italic">{address}</address>
        <p>
          Bei Fragen rund um das Thema Datenschutz wenden Sie sich bitte an
          unseren Datenschutzbeauftragten:
          <br />
          <br />
          {privacyOfficer}
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          3. Erfassung von allgemeinen Daten und Informationen
        </h2>
        <p>
          Die Internetseite der {companyName} erfasst mit jedem Aufruf der
          Internetseite durch eine betroffene Person oder ein automatisiertes
          System eine Reihe von allgemeinen Daten und Informationen. Diese
          allgemeinen Daten und Informationen werden in den Logfiles des Servers
          gespeichert. Erfasst werden können die:
        </p>
        <ul className="list-disc pl-6">
          <li>verwendeten Browsertypen und Versionen</li>
          <li>das vom zugreifenden System verwendete Betriebssystem</li>
          <li>
            die Internetseite, von welcher ein zugreifendes System auf unsere
            Internetseite gelangt (sogenannte Referrer)
          </li>
          <li>
            die Unterwebseiten, welche über ein zugreifendes System auf unserer
            Internetseite angesteuert werden
          </li>
          <li>
            das Datum und die Uhrzeit eines Zugriffs auf die Internetseite
          </li>
          <li>eine Internet-Protokoll-Adresse (IP-Adresse)</li>
          <li>der Internet-Service-Provider des zugreifenden Systems</li>
          <li>
            sonstige ähnliche Daten und Informationen, die der Gefahrenabwehr im
            Falle von Angriffen auf unsere informationstechnologischen Systeme
            dienen
          </li>
        </ul>
        <p>Diese Informationen werden vielmehr benötigt, um</p>
        <ol className="list-decimal pl-6">
          <li>die Inhalte unserer Internetseite korrekt auszuliefern, </li>
          <li>
            die Inhalte unserer Internetseite sowie die Werbung für diese zu
            optimieren,
          </li>
          <li>
            die dauerhafte Funktionsfähigkeit unserer
            informationstechnologischen Systeme und der Technik unserer
            Internetseite zu gewährleisten sowie
          </li>
          <li>
            um Strafverfolgungsbehörden im Falle eines Cyberangriffes die zur
            Strafverfolgung notwendigen Informationen bereitzustellen. Diese
            anonym erhobenen Daten und Informationen werden durch den
            datenschutzrechtlich Verantwortlichen daher einerseits statistisch
            und ferner mit dem Ziel ausgewertet, den Datenschutz und die
            Datensicherheit in unserem Unternehmen zu erhöhen, um letztlich ein
            optimales Schutzniveau für die von uns verarbeiteten
            personenbezogenen Daten sicherzustellen. Die anonymen Daten der
            Server-Logfiles werden getrennt von allen durch eine betroffene
            Person angegebenen personenbezogenen Daten gespeichert
          </li>
        </ol>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          4. Google TAG Manager
        </h2>
        <p>
          <strong>Art und Zweck der Verarbeitung:</strong> Wir verwenden den
          Dienst namens Google Tag Manager von Google. "Google" ist eine
          Firmengruppe und besteht aus den Firmen Google Ireland Ltd. (Anbieter
          des Dienstes), Gordon House, Barrow Street, Dublin 4, Irland sowie
          Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA
          sowie andere verbundene Unternehmen der Google LLC. <br />
          Wir haben einen Auftragsverarbeitungsvertrag mit Google abgeschlossen.
          Der Google Tag Manager ist ein Hilfsdienst und verarbeitet selbst
          personenbezogenen Daten nur zu technisch notwendigen Zwecken. Der
          Google Tag Manager sorgt für das Laden anderer Komponenten, die
          ihrerseits unter Umständen Daten erfassen. Der Google Tag Manager
          greift nicht auf diese Daten zu.
          <br />
          Weitere Informationen zum Google Tag Manager finden Sie in den
          Datenschutzbestimmungen von Google.
          <br /> Bitte beachten Sie, dass amerikanische Behörden aufgrund
          amerikanischer Gesetze wie dem Cloud Act möglicherweise Zugriff auf
          personenbezogene Daten erhalten könnten, die beim Einbinden dieses
          Dienstes zwangsläufig aufgrund des Internet Protokolls (TCP) mit
          Google ausgetauscht werden.
        </p>
        <p>
          <strong>Rechtsgrundlage:</strong> Rechtsgrundlage für die Einbindung
          von Google und dem damit verbundenen Datentransfer zu Google ist Ihre
          Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).
        </p>
        <p>
          <strong>Empfänger:</strong> Bei jedem Besuch unsere Webseite werden
          personenbezogene Daten, einschließlich Ihrer IP-Adresse an Google in
          die USA übertragen. Diese personenbezogenen Datenwerden durch Google
          gespeichert. Google gibt diese über das technische Verfahren erhobenen
          personenbezogenen Daten unter Umständen an Dritte weiter. Unser
          Unternehmen enthält keine Informationen von Google, mittels derer die
          betroffene Person identifiziert werden könnte
        </p>
        <p>
          <strong>Speicherdauer:</strong> Diese Cookies verlieren nach 30 Tagen
          ihre Gültigkeit und dienen nicht der persönlichen Identifizierung.
        </p>
        <p>
          <strong>Drittlandtransfer:</strong> Google verarbeitet Ihre Daten in
          den USA und hat sich dem EU_US Privacy Shield unterworfen{" "}
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://www.privacyshield.gov/EU-US-Framework"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.privacyshield.gov/EU-US-Framework
          </a>
          .
        </p>
        <p>
          <strong>Widerruf der Einwilligung:</strong> Möchten Sie nicht am
          Tracking teilnehmen, können Sie das hierfür erforderliche Setzen eines
          Cookies ablehnen – etwa per Browser-Einstellung, die das automatische
          Setzen von Cookies generell deaktiviert oder Ihren Browser so
          einstellen, dass Cookies von der Domain „googleleadservices.com“
          blockiert werden.
          <br />
          Bitte beachten Sie, dass Sie die Opt-out-Cookies nicht löschen dürfen,
          solange Sie keine Aufzeichnung von Messdaten wünschen. Haben Sie alle
          Ihre Cookies im Browsergelöscht, müssen Sie das jeweilige Opt-out
          Cookie erneut setzen.
        </p>
        <p>
          <strong>Bereitstellung vorgeschrieben oder erforderlich:</strong> Die
          Bereitstellung Ihrer personenbezogenen Daten erfolgt freiwillig,
          allein auf Basis Ihrer Einwilligung. Sofern Sie den Zugriff
          unterbinden, kann es hierdurch zu Funktionseinschränkungen auf der
          Website kommen.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          5. Einsatz von Amazon Web Services (AWS)
        </h2>
        <p>
          <strong>Art und Zweck der Verarbeitung:</strong> Anbieter ist die
          Amazon Web Services EMEA SARL, 38 Avenue John F. Kennedy, 1855
          Luxemburg (nachfolgend AWS).
          <br />
          Wenn Sie unsere Produkte nutzen, werden Ihre personenbezogenen Daten
          auf den Servern von AWS verarbeitet. Hierbei können auch
          personenbezogene Daten an das Mutterunternehmen von AWS in die USA
          übermittelt werden. Die Datenübertragung in die USA wird auf die
          EU-Standardvertragsklauseln gestützt. Details finden Sie hier:
          <br />
          <a
            className="underline underline-offset-2 hover:decoration-primary break-all"
            href="https://aws.amazon.com/de/blogs/security/aws-gdpr-data-processing-addendum/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://aws.amazon.com/de/blogs/security/aws-gdpr-data-processing-addendum/
          </a>
          <br />
          Weitere Informationen entnehmen Sie der Datenschutzerklärung von AWS:{" "}
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://aws.amazon.com/de/privacy/?nc1=f_pr"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://aws.amazon.com/de/privacy/?nc1=f_pr
          </a>
        </p>
        <p>
          <strong>Rechtsgrundlage:</strong> Die Verwendung von AWS erfolgt auf
          Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Sofern eine entsprechende
          Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich
          auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG,
          soweit die Einwilligung die Speicherung von Cookies oder den Zugriff
          auf Informationen im Endgerät des Nutzers (z. B.
          Device-Fingerprinting) im Sinne des TTDSG umfasst. Die Einwilligung
          ist jederzeit widerrufbar.
        </p>
        <p>
          <strong>Drittlandtransfer:</strong> Das Unternehmen verfügt über eine
          Zertifizierung nach dem „EU-US Data Privacy Framework“ (DPF). Der DPF
          ist ein Übereinkommen zwischen der Europäischen Union und den USA, der
          die Einhaltung europäischer Datenschutzstandards bei
          Datenverarbeitungen in den USA gewährleisten soll. Jedes nach dem DPF
          zertifizierte Unternehmen verpflichtet sich, diese
          Datenschutzstandards einzuhalten. Weitere Informationen hierzu
          erhalten Sie vom Anbieter unter folgendem Link:
          <br />
          <a
            className="underline underline-offset-2 hover:decoration-primary break-all"
            href="https://www.dataprivacyframework.gov/s/participant-search/participant-detail?contact=true&id=a2zt0000000TOWQAA4&status=Active"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.dataprivacyframework.gov/s/participant-search/participant-detail?contact=true&id=a2zt0000000TOWQAA4&status=Active
          </a>
        </p>
        <p>
          <strong>Auftragsverarbeitung:</strong> Wir haben einen Vertrag über
          Auftragsverarbeitung (AVV) zur Nutzung des oben genannten Dienstes
          geschlossen. Hierbei handelt es sich um einen datenschutzrechtlich
          vorgeschriebenen Vertrag, der gewährleistet, dass dieser die
          personenbezogenen Daten unserer Websitebesucher nur nach unseren
          Weisungen und unter Einhaltung der DSGVO verarbeitet.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          6. Registrierung in unserer WebApp
        </h2>
        <p>
          Die betroffene Person hat die Möglichkeit, sich in der WebApp des für
          die Verarbeitung Verantwortlichen unter Angabe von personenbezogenen
          Daten zu registrieren. Welche personenbezogenen Daten dabei an den für
          die Verarbeitung Verantwortlichen übermittelt werden, ergibt sich aus
          der jeweiligen Eingabemaske, die für die Registrierung verwendet wird.
          Die von der betroffenen Person eingegebenen personenbezogenen Daten
          werden ausschließlich für die interne Verwendung bei dem für die
          Verarbeitung Verantwortlichen und für eigene Zwecke erhoben und
          gespeichert. Der für die Verarbeitung Verantwortliche kann die
          Weitergabe an einen oder mehrere Auftragsverarbeiter, beispielsweise
          einen Paketdienstleister, veranlassen, der die personenbezogenen Daten
          ebenfalls ausschließlich für eine interne Verwendung, die dem für die
          Verarbeitung Verantwortlichen zuzurechnen ist, nutzt. Durch eine
          Registrierung in der WebApp des für die Verarbeitung Verantwortlichen
          wird ferner die vom Internet-Service-Provider (ISP) der betroffenen
          Person vergebene IP-Adresse, das Datum sowie die Uhrzeit der
          Registrierung gespeichert. Die Speicherung dieser Daten erfolgt vor
          dem Hintergrund, dass nur so der Missbrauch unserer Dienste verhindert
          werden kann, und diese Daten im Bedarfsfall ermöglichen, begangene
          Straftaten aufzuklären. Insofern ist die Speicherung dieser Daten zur
          Absicherung des für die Verarbeitung Verantwortlichen erforderlich.
          Eine Weitergabe dieser Daten an Dritte erfolgt grundsätzlich nicht,
          sofern keine gesetzliche Pflicht zur Weitergabe besteht oder die
          Weitergabe der Strafverfolgung dient.
          <br />
          Die Registrierung der betroffenen Person unter freiwilliger Angabe
          personenbezogener Daten dient dem für die Verarbeitung
          Verantwortlichen dazu, der betroffenen Person Inhalte oder Leistungen
          anzubieten, die aufgrund der Natur der Sache nur registrierten
          Benutzern angeboten werden können. Registrierten Personen steht die
          Möglichkeit frei, die bei der Registrierung angegebenen
          personenbezogenen Daten jederzeit abzuändern oder vollständig aus dem
          Datenbestand des für die Verarbeitung Verantwortlichen löschen zu
          lassen.
          <br />
          Der für die Verarbeitung Verantwortliche erteilt jeder betroffenen
          Person jederzeit auf Anfrage Auskunft darüber, welche
          personenbezogenen Daten über die betroffene Person gespeichert sind.
          Ferner berichtigt oder löscht der für die Verarbeitung Verantwortliche
          personenbezogene Daten auf Wunsch oder Hinweis der betroffenen Person,
          soweit dem keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
          Die Gesamtheit der Mitarbeiter des für die Verarbeitung
          Verantwortlichen stehen der betroffenen Person in diesem Zusammenhang
          als Ansprechpartner zur Verfügung.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          7. Kontaktmöglichkeit über die WebApp
        </h2>
        <p>
          Die WebApp enthält aufgrund von gesetzlichen Vorschriften Angaben, die
          eine schnelle elektronische Kontaktaufnahme zu unserem Unternehmen
          sowie eine unmittelbare Kommunikation mit uns ermöglichen, was
          ebenfalls eine allgemeine Adresse der sogenannten elektronischen Post
          (E-Mail-Adresse) umfasst. Sofern eine betroffene Person per E-Mail
          oder über ein Kontaktformular den
          <br />
          Kontakt, mit dem für die Verarbeitung Verantwortlichen aufnimmt,
          werden die von der betroffenen Person übermittelten personenbezogenen
          Daten automatisch gespeichert. Solche auf freiwilliger Basis von einer
          betroffenen Person an den für die Verarbeitung Verantwortlichen
          übermittelten personenbezogenen Daten werden für Zwecke der
          Bearbeitung oder der Kontaktaufnahme zur betroffenen Person
          gespeichert. Es erfolgt keine Weitergabe dieser personenbezogenen
          Daten an Dritte.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          8. Routinemäßige Löschung und Sperrung von personenbezogenen Daten
        </h2>
        <p>
          Der für die Verarbeitung Verantwortliche verarbeitet und speichert
          personenbezogene Daten der betroffenen Person nur für den Zeitraum,
          der zur Erreichung des Speicherungszwecks erforderlich ist oder sofern
          dies durch den Europäischen Richtlinien- und Verordnungsgeber oder
          einen anderen Gesetzgeber in Gesetzen oder Vorschriften, welchen der
          für die Verarbeitung Verantwortliche unterliegt, vorgesehen wurde.
          <br />
          Entfällt der Speicherungszweck oder läuft eine vom Europäischen
          Richtlinien- und Verordnungsgeber oder einem anderen zuständigen
          Gesetzgeber vorgeschriebene Speicherfrist ab, werden die
          personenbezogenen Daten routinemäßig und entsprechend den gesetzlichen
          Vorschriften gesperrt oder gelöscht.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          9. Zahlungsart: Datenschutzbestimmungen zu Stripe als Zahlungsart
        </h2>
        <p>
          Wir bieten die Möglichkeit, den Zahlungsvorgang über den
          Zahlungsdienstleister Stripe, ℅ Legal Process, 510,Townsend St., San
          Francisco, CA 94103 (Stripe) abzuwickeln. Dies entspricht unserem
          berechtigten Interesse, eine effiziente und sichere Zahlungsmethode
          anzubieten (Art. 6 Abs. 1 lit. f DSGVO). In dem Zusammenhang geben wir
          folgende Daten an Stripe weiter, soweit es für die Vertragserfüllung
          erforderlich ist (Art. 6 Abs. 1 lit b. DSGVO).
        </p>
        <ul className="list-disc pl-6">
          <li>Name des Karteninhabers</li>
          <li>E-Mail-Adresse</li>
          <li>Kundennummer</li>
          <li>Bestellnummer</li>
          <li>Bankverbindung</li>
          <li>Kreditkartendaten</li>
          <li>Gültigkeitsdauer der Kreditkarte</li>
          <li>Prüfnummer der Kreditkarte (CVC)</li>
          <li>Datum und Uhrzeit der Transaktion</li>
          <li>Transaktionssumme</li>
          <li>Name des Anbieters</li>
          <li>Ort</li>
        </ul>
        <p>
          Die Verarbeitung der unter diesem Abschnitt angegebenen Daten ist
          weder gesetzlich noch vertraglich vorgeschrieben. Ohne die
          Übermittlung Ihrer personenbezogenen Daten können wir eine Zahlung
          über Stripe nicht durchführen.
          <br />
          Stripe nimmt bei Datenverarbeitungstätigkeiten eine Doppelrolle als
          Verantwortlicher und Auftragsverarbeiter ein. Als Verantwortlicher
          verwendet Stripe Ihre übermittelten Daten zur Erfüllung
          regulatorischer Verpflichtungen. Dies entspricht dem berechtigten
          Interesse Stripes (gem. Art. 6 Abs. 1 lit. f DSGVO) und dient der
          Vertragsdurchführung (gem. Art. 6 Abs. 1 lit. b DSGVO). Wir haben auf
          diesen Prozess keinen Einfluss.
          <br />
          Als Auftragsverarbeiter fungiert Stripe, um Transaktionen innerhalb
          der Zahlungsnetzwerke abschließen zu können. Im Rahmen des
          Auftragsverarbeitungsverhältnisses wird Stripe ausschließlich nach
          unserer Weisung tätig und wurde im Sinne des Art. 28 DSGVO vertraglich
          verpflichtet, die datenschutzrechtlichen Bestimmungen einzuhalten.
          <br />
          Stripe hat Compliance-Maßnahmen für internationale Datenübermittlungen
          umgesetzt. Diese gelten für alle weltweiten Aktivitäten, bei denen
          Stripe personenbezogene Daten von natürlichen Personen in der EU
          verarbeitet. Diese Maßnahmen basieren auf den
          EU-Standardvertragsklauseln (SCCs).
          <br />
          Weitere Informationen zu Widerspruchs- und Beseitigungsmöglichkeiten
          gegenüber Stripe finden Sie unter:
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://stripe.com/privacy-center/legal"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://stripe.com/privacy-center/legal
          </a>
          <br />
          Ihre Daten werden von uns bis zum Abschluss der Zahlungsabwicklung
          gespeichert. Dazu zählt auch der Zeitraum der für die Bearbeitung von
          Rückerstattungen, Forderungsmanagement und Betrugsprävention
          erforderlich ist. Es gilt die gestzliche Aufbewahrungsfrist.
          <br />
          <br />
          Die Europäische Betreibergesellschaft ist Stripe Payments Europe
          Limited 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02
          H210, Ireland Attention: Stripe Legal
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          10. Rechte der betroffenen Person
        </h2>
        <ul className="list-disc pl-6">
          <li>
            <p>
              <strong>Recht auf Bestätigung:</strong> Jede betroffene Person hat
              das vom Europäischen Richtlinien- und Verordnungsgeber eingeräumte
              Recht, von dem für die Verarbeitung Verantwortlichen eine
              Bestätigung darüber zu verlangen, ob sie betreffende
              personenbezogene Daten verarbeitet werden. Möchte eine betroffene
              Person dieses Bestätigungsrecht in Anspruch nehmen, kann sie sich
              hierzu jederzeit an einen Mitarbeiter des für die Verarbeitung
              Verantwortlichen wenden.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Auskunft</strong> Jede von der Verarbeitung
              personenbezogener Daten betroffene Person hat das vom Europäischen
              Richtlinien- und Verordnungsgeber gewährte Recht, jederzeit von
              dem für die Verarbeitung Verantwortlichen unentgeltliche Auskunft
              über die zu seiner Person gespeicherten personenbezogenen Daten
              und eine Kopie dieser Auskunft zu erhalten. Ferner hat der
              Europäische Richtlinien- und Verordnungsgeber der betroffenen
              Person Auskunft über folgende Informationen zugestanden:
            </p>
            <ul className="list-disc pl-6 ">
              <li>die Verarbeitungszwecke</li>
              <li>
                die Kategorien personenbezogener Daten, die verarbeitet werden
              </li>

              <li>
                die Empfänger oder Kategorien von Empfängern, gegenüber denen
                die personenbezogenen Daten offengelegt worden sind oder noch
                offengelegt werden, insbesondere bei Empfängern in Drittländern
                oder bei internationalen Organisationen
              </li>

              <li>
                falls möglich die geplante Dauer, für die die personenbezogenen
                Daten gespeichert werden, oder, falls dies nicht möglich ist,
                die Kriterien für die Festlegung dieser Dauer
              </li>

              <li>
                das Bestehen eines Rechts auf Berichtigung oder Löschung der sie
                betreffenden personenbezogenen Daten oder auf Einschränkung der
                Verarbeitung durch den Verantwortlichen oder eines
                Widerspruchsrechts gegen diese Verarbeitung
              </li>

              <li>
                das Bestehen eines Beschwerderechts bei einer Aufsichtsbehörde
                wenn die personenbezogenen Daten nicht bei der betroffenen
                Person erhoben werden:
              </li>

              <li>
                Alle verfügbaren Informationen über die Herkunft der Daten
              </li>

              <li>
                das Bestehen einer automatisierten Entscheidungsfindung
                einschließlich Profiling gemäß Artikel 22 Abs.1 und 4 DSGVO und
                — zumindest in diesen Fällen — aussagekräftige Informationen
                über die involvierte Logik sowie die Tragweite und die
                angestrebten Auswirkungen einer derartigen Verarbeitung für die
                betroffene Person
              </li>
            </ul>
            <p>
              Ferner steht der betroffenen Person ein Auskunftsrecht darüber zu,
              ob personenbezogene Daten an ein Drittland oder an eine
              internationale Organisation übermittelt wurden. Sofern dies der
              Fall ist, so steht der betroffenen Person im Übrigen das Recht zu,
              Auskunft über die geeigneten Garantien im Zusammenhang mit der
              Übermittlung zu erhalten.
              <br />
              Möchte eine betroffene Person dieses Auskunftsrecht in Anspruch
              nehmen, kann sie sich hierzu jederzeit an einen Mitarbeiter des
              für die Verarbeitung Verantwortlichen wenden.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Berichtigung</strong> Jede von der Verarbeitung
              personenbezogener Daten betroffene Person hat das vom Europäischen
              Richtlinien- und Verordnungsgeber gewährte Recht, die
              unverzügliche Berichtigung sie betreffender unrichtiger
              personenbezogener Daten zu verlangen. Ferner steht der betroffenen
              Person das Recht zu, unter Berücksichtigung der Zwecke der
              Verarbeitung, die Vervollständigung unvollständiger
              personenbezogener Daten — auch mittels einer ergänzenden Erklärung
              — zu verlangen.
              <br />
              Möchte eine betroffene Person dieses Berichtigungsrecht in
              Anspruch nehmen, kann sie sich hierzu jederzeit an einen
              Mitarbeiter des für die Verarbeitung Verantwortlichen wenden.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Löschung (Recht auf Vergessen werden)</strong>{" "}
              Jede von der Verarbeitung personenbezogener Daten betroffene
              Person hat das vom Europäischen Richtlinien- und Verordnungsgeber
              gewährte Recht, von dem Verantwortlichen zu verlangen, dass die
              sie betreffenden personenbezogenen Daten unverzüglich gelöscht
              werden, sofern einer der folgenden Gründe zutrifft und soweit die
              Verarbeitung nicht erforderlich ist:
              <br />
              Die personenbezogenen Daten wurden für solche Zwecke erhoben oder
              auf sonstige Weise verarbeitet, für welche sie nicht mehr
              notwendig sind. Die betroffene Person widerruft ihre Einwilligung,
              auf die sich die Verarbeitung gemäß Art. 6 Abs. 1 Buchstabe a
              DSGVO oder Art. 9 Abs. 2 Buchstabe a DSGVO stützte, und es fehlt
              an einer anderweitigen Rechtsgrundlage für die Verarbeitung. Die
              betroffene Person legt gemäß Art. 21 Abs. 1 DSGVO Widerspruch
              gegen die Verarbeitung ein, und es liegen keine vorrangigen
              berechtigten Gründe für die Verarbeitung vor, oder die betroffene
              Person legt gemäß Art. 21 Abs. 2 DSGVO Widerspruch gegen die
              Verarbeitung ein. Die personenbezogenen Daten wurden unrechtmäßig
              verarbeitet. Die Löschung der personenbezogenen Daten ist zur
              Erfüllung einer rechtlichen Verpflichtung nach dem Unionsrecht
              oder dem Recht der Mitgliedstaaten erforderlich, dem der
              Verantwortliche unterliegt. Die personenbezogenen Daten wurden in
              Bezug auf angebotene Dienste der Informationsgesellschaft gemäß
              Art. 8 Abs. 1 DSGVO erhoben. Sofern einer der oben genannten
              Gründe zutrifft und eine betroffene Person die Löschung von
              personenbezogenen Daten, die bei dem datenschutzrechtlich
              Verantwortlichen gespeichert sind, veranlassen möchte, kann sie
              sich hierzu jederzeit an einen Mitarbeiter des für die
              Verarbeitung
              <br />
              Verantwortlichen wenden. Der Mitarbeiter des datenschutzrechtlich
              Verantwortlichen wird veranlassen, dass dem Löschverlangen
              unverzüglich nachgekommen wird.
              <br />
              Wurden die personenbezogenen Daten von dem datenschutzrechtlich
              Verantwortlichen öffentlich gemacht und ist unser Unternehmen als
              Verantwortlicher gemäß Art. 17 Abs. 1 DSGVO zur Löschung der
              personenbezogenen Daten verpflichtet, so trifft der
              datenschutzrechtlich Verantwortliche unter Berücksichtigung der
              verfügbaren Technologie und der Implementierungskosten angemessene
              Maßnahmen, auch technischer Art, um andere für die
              Datenverarbeitung Verantwortliche, welche die veröffentlichten
              personenbezogenen Daten verarbeiten, darüber in Kenntnis zu
              setzen, dass die betroffene Person von diesen anderen für die
              Datenverarbeitung Verantwortlichen die Löschung sämtlicher Links
              zu diesen personenbezogenen Daten oder von Kopien oder
              Replikationen dieser personenbezogenen Daten verlangt hat, soweit
              die Verarbeitung nicht erforderlich ist. Der Mitarbeiter des
              datenschutzrechtlich Verantwortlichen wird im Einzelfall das
              Notwendige veranlassen.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Einschränkung der Verarbeitung</strong> Jede von
              der Verarbeitung personenbezogener Daten betroffene Person hat das
              vom Europäischen Richtlinien- und Verordnungsgeber gewährte Recht,
              von dem Verantwortlichen die Einschränkung der Verarbeitung zu
              verlangen, wenn eine der folgenden Voraussetzungen gegeben ist:
              <br />
              Die Richtigkeit der personenbezogenen Daten wird von der
              betroffenen Person bestritten, und zwar für eine Dauer, die es dem
              Verantwortlichen ermöglicht, die Richtigkeit der personenbezogenen
              Daten zu überprüfen. Die Verarbeitung ist unrechtmäßig, die
              betroffene Person lehnt die Löschung der personenbezogenen Daten
              ab und verlangt stattdessen die Einschränkung der Nutzung der
              personenbezogenen Daten. Der Verantwortliche benötigt die
              personenbezogenen Daten für die Zwecke der Verarbeitung nicht
              länger, die betroffene Person benötigt sie jedoch zur
              Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
              Die betroffene Person hat Widerspruch gegen die Verarbeitung gem.
              Art. 21 Abs. 1 DSGVO eingelegt und es steht noch nicht fest, ob
              die berechtigten Gründe des Verantwortlichen gegenüber denen der
              betroffenen Person überwiegen. Sofern eine der oben genannten
              Voraussetzungen gegeben ist und eine betroffene Person die
              Einschränkung von personenbezogenen Daten, die bei dem
              datenschutzrechtlich Verantwortlichen gespeichert sind, verlangen
              möchte, kann sie sich hierzu jederzeit an einen Mitarbeiter des
              für die Verarbeitung Verantwortlichen wenden. Der Mitarbeiter des
              datenschutzrechtlich Verantwortlichen wird die Einschränkung der
              Verarbeitung veranlassen.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Datenübertragbarkeit</strong> Jede von der
              Verarbeitung personenbezogener Daten betroffene Person hat das vom
              Europäischen Richtlinien- und Verordnungsgeber gewährte Recht, die
              sie betreffenden personenbezogenen Daten, welche durch die
              betroffene Person einem Verantwortlichen bereitgestellt wurden, in
              einem strukturierten, gängigen und maschinenlesbaren Format zu
              erhalten. Sie hat außerdem das Recht, diese Daten einem anderen
              Verantwortlichen ohne Behinderung durch den Verantwortlichen, dem
              die personenbezogenen Daten bereitgestellt wurden, zu übermitteln,
              sofern die Verarbeitung auf der Einwilligung gemäß Art. 6 Abs. 1
              Buchstabe a DSGVO oder Art. 9 Abs. 2 Buchstabe a DSGVO oder auf
              einem Vertrag gemäß Art. 6 Abs. 1 Buchstabe b DSGVO beruht und die
              Verarbeitung mithilfe automatisierter Verfahren erfolgt, sofern
              die Verarbeitung nicht für die Wahrnehmung einer Aufgabe
              erforderlich ist, die im öffentlichen Interesse liegt oder in
              Ausübung öffentlicher Gewalt erfolgt, welche dem Verantwortlichen
              übertragen wurde.
              <br />
              Ferner hat die betroffene Person bei der Ausübung ihres Rechts auf
              Datenübertragbarkeit gemäß Art. 20 Abs. 1 DSGVO das Recht, zu
              erwirken, dass die personenbezogenen Daten direkt von einem
              Verantwortlichen an einen anderen Verantwortlichen übermittelt
              werden, soweit dies technisch machbar ist und sofern hiervon nicht
              die Rechte und Freiheiten anderer Personen beeinträchtigt werden.
              <br />
              Zur Geltendmachung des Rechts auf Datenübertragbarkeit kann sich
              die betroffene Person jederzeit an einen Mitarbeiter des
              datenschutzrechtlich Verantwortlichen wenden.
            </p>
          </li>
          <li>
            <p>
              <strong>Recht auf Widerspruch:</strong> Jede von der Verarbeitung
              personenbezogener Daten betroffene Person hat das vom Europäischen
              Richtlinien- und Verordnungsgeber gewährte Recht, aus Gründen, die
              sich aus ihrer besonderen Situation ergeben, jederzeit gegen die
              Verarbeitung sie betreffender personenbezogener Daten, die
              aufgrund von Art. 6 Abs. 1 Buchstaben e oder f DSGVO erfolgt,
              Widerspruch einzulegen. Dies gilt auch für ein auf diese
              Bestimmungen gestütztes Profiling. <br />
              Der datenschutzrechtlich Verantwortliche verarbeitet die
              personenbezogenen Daten im Falle des Widerspruchs nicht mehr, es
              sei denn, wir können zwingende schutzwürdige Gründe für die
              Verarbeitung nachweisen, die den Interessen, Rechten und
              Freiheiten der betroffenen Person überwiegen, oder die
              Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung
              von Rechtsansprüchen.
              <br />
              Verarbeitet der datenschutzrechtlich Verantwortliche
              personenbezogene Daten, um Direktwerbung zu betreiben, so hat die
              betroffene Person das Recht, jederzeit Widerspruch gegen die
              Verarbeitung der personenbezogenen Daten zum Zwecke derartiger
              Werbung einzulegen. Dies gilt auch für das Profiling, soweit es
              mit solcher Direktwerbung in Verbindung steht. Widerspricht die
              betroffene Person gegenüber dem datenschutzrechtlich
              Verantwortlichen der Verarbeitung für Zwecke der Direktwerbung, so
              wird der datenschutzrechtlich Verantwortliche die
              personenbezogenen Daten nicht mehr für diese Zwecke verarbeiten.
              <br />
              Zudem hat die betroffene Person das Recht, aus Gründen, die sich
              aus ihrer besonderen Situation ergeben, gegen die sie betreffende
              Verarbeitung personenbezogener Daten, die bei dem
              datenschutzrechtlich Verantwortlichen zu wissenschaftlichen oder
              historischen Forschungszwecken oder zu statistischen Zwecken gemäß
              Art. 89 Abs. 1 DSGVO erfolgen, Widerspruch einzulegen, es sei
              denn, eine solche Verarbeitung ist zur Erfüllung einer im
              öffentlichen Interesse liegenden Aufgabe erforderlich.
              <br />
              Zur Ausübung des Rechts auf Widerspruch kann sich die betroffene
              Person direkt jeden Mitarbeiter des datenschutzrechtlich
              Verantwortlichen oder einen anderen Mitarbeiter wenden. Der
              betroffenen Person steht es ferner frei, im Zusammenhang mit der
              Nutzung von Diensten der Informationsgesellschaft, ungeachtet der
              Richtlinie 2002/58/EG, ihr Widerspruchsrecht mittels
              automatisierter Verfahren auszuüben, bei denen technische
              Spezifikationen verwendet werden.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Automatisierte Entscheidungen im Einzelfall einschließlich
                Profiling
              </strong>{" "}
              Jede von der Verarbeitung personenbezogener Daten betroffene
              Person hat das vom Europäischen Richtlinien- und Verordnungsgeber
              gewährte Recht, nicht einer ausschließlich auf einer
              automatisierten Verarbeitung — einschließlich Profiling —
              beruhenden Entscheidung unterworfen zu werden, die ihr gegenüber
              rechtliche Wirkung entfaltet oder sie in ähnlicher Weise erheblich
              beeinträchtigt, sofern die Entscheidung (1) nicht für den
              Abschluss oder die Erfüllung eines Vertrags zwischen der
              betroffenen Person und dem Verantwortlichen erforderlich ist, oder
              (2) aufgrund von Rechtsvorschriften der Union oder der
              Mitgliedstaaten, denen der Verantwortliche unterliegt, zulässig
              ist und diese Rechtsvorschriften angemessene Maßnahmen zur Wahrung
              der Rechte und Freiheiten sowie der berechtigten Interessen der
              betroffenen Person enthalten oder (3) mit ausdrücklicher
              Einwilligung der betroffenen Person erfolgt.
              <br />
              Ist die Entscheidung (1) für den Abschluss oder die Erfüllung
              eines Vertrags zwischen der betroffenen Person und dem
              Verantwortlichen erforderlich oder (2) erfolgt sie mit
              ausdrücklicher Einwilligung der betroffenen Person, trifft der
              datenschutzrechtlich Verantwortliche angemessene Maßnahmen, um die
              Rechte und Freiheiten sowie die berechtigten Interessen der
              betroffenen Person zu wahren, wozu mindestens das Recht auf
              Erwirkung des Eingreifens einer Person seitens des
              Verantwortlichen, auf Darlegung des eigenen Standpunkts und auf
              Anfechtung der Entscheidung gehört.
              <br />
              Möchte die betroffene Person Rechte mit Bezug auf automatisierte
              Entscheidungen geltend machen, kann sie sich hierzu jederzeit an
              einen Mitarbeiter des für die Verarbeitung Verantwortlichen
              wenden.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Recht auf Widerruf einer datenschutzrechtlichen Einwilligung
              </strong>{" "}
              Jede von der Verarbeitung personenbezogener Daten betroffene
              Person hat das vom Europäischen Richtlinien- und Verordnungsgeber
              gewährte Recht, eine Einwilligung zur Verarbeitung
              personenbezogener Daten jederzeit zu widerrufen.
              <br />
              Möchte die betroffene Person ihr Recht auf Widerruf einer
              Einwilligung geltend machen, kann sie sich hierzu jederzeit an
              einen Mitarbeiter des für die Verarbeitung Verantwortlichen
              wenden.
            </p>
          </li>
        </ul>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          11. Rechtsgrundlage der Verarbeitung
        </h2>
        <p>
          Art. 6 I lit. a DSGVO dient unserem Unternehmen als Rechtsgrundlage
          für Verarbeitungsvorgänge, bei denen wir eine Einwilligung für einen
          bestimmten Verarbeitungszweck einholen. Ist die Verarbeitung
          personenbezogener Daten zur Erfüllung eines Vertrags, dessen
          Vertragspartei die betroffene Person ist, erforderlich, wie dies
          beispielsweise bei Verarbeitungsvorgängen der Fall ist, die für eine
          Lieferung von Waren oder die Erbringung einer sonstigen Leistung oder
          Gegenleistung notwendig sind, so beruht die Verarbeitung auf Art. 6 I
          lit. b DSGVO. Gleiches gilt für solche Verarbeitungsvorgänge, die zur
          Durchführung vorvertraglicher Maßnahmen erforderlich sind, etwa in
          Fällen von Anfragen zu unseren Produkten oder Leistungen. Unterliegt
          unser Unternehmen einer rechtlichen Verpflichtung durch welche eine
          Verarbeitung von personenbezogenen Daten erforderlich wird, wie
          beispielsweise zur Erfüllung steuerlicher Pflichten, so basiert die
          Verarbeitung auf Art. 6 I lit. c DSGVO. In seltenen Fällen könnte die
          Verarbeitung von personenbezogenen Daten erforderlich werden, um
          lebenswichtige Interessen der betroffenen Person oder einer anderen
          natürlichen Person zu schützen. Dies wäre beispielsweise der Fall,
          wenn ein Besucher in unserem Betrieb verletzt werden würde und
          daraufhin sein Name, sein Alter, seine Krankenkassendaten oder
          sonstige lebenswichtige Informationen an einen Arzt, ein Krankenhaus
          oder sonstige Dritte weitergegeben werden müssten. Dann würde die
          Verarbeitung auf Art. 6 I lit. d DSGVO beruhen. Letztlich könnten
          Verarbeitungsvorgänge auf Art. 6 I lit. f DSGVO beruhen. Auf dieser
          Rechtsgrundlage basieren Verarbeitungsvorgänge, die von keiner der
          vorgenannten Rechtsgrundlagen erfasst werden, wenn die Verarbeitung
          zur Wahrung eines berechtigten Interesses unseres Unternehmens oder
          eines Dritten erforderlich ist, sofern die Interessen, Grundrechte und
          Grundfreiheiten des Betroffenen nicht überwiegen. Solche
          Verarbeitungsvorgänge sind uns insbesondere deshalb gestattet, weil
          sie durch den Europäischen Gesetzgeber besonders erwähnt
          <br />
          wurden. Er vertrat insoweit die Auffassung, dass ein berechtigtes
          Interesse anzunehmen sein könnte, wenn die betroffene Person ein Kunde
          des Verantwortlichen ist (Erwägungsgrund 47 Satz 2 DSGVO).
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          12. Berechtigte Interessen an der Verarbeitung, die von dem
          Verantwortlichen oder einem Dritten verfolgt werden
        </h2>
        <p>
          Basiert die Verarbeitung personenbezogener Daten auf Artikel 6 I lit.
          f DSGVO ist unser berechtigtes Interesse die Durchführung unserer
          Geschäftstätigkeit zugunsten des Wohlergehens all unserer Mitarbeiter
          und unserer Anteilseigner.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          13. Dauer, für die die personenbezogenen Daten gespeichert werden
        </h2>
        <p>
          Das Kriterium für die Dauer der Speicherung von personenbezogenen
          Daten ist die jeweilige gesetzliche Aufbewahrungsfrist. Nach Ablauf
          der Frist werden die entsprechenden Daten routinemäßig gelöscht,
          sofern sie nicht mehr zur Vertragserfüllung oder Vertragsanbahnung
          erforderlich sind
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          14. Gesetzliche oder vertragliche Vorschriften zur Bereitstellung der
          personenbezogenen Daten; Erforderlichkeit für den Vertragsabschluss;
          Verpflichtung der betroffenen Person, die personenbezogenen Daten
          bereitzustellen; mögliche Folgen der Nichtbereitstellung
        </h2>
        <p>
          Wir klären Sie darüber auf, dass die Bereitstellung personenbezogener
          Daten zum Teil gesetzlich vorgeschrieben ist (z.B. Steuervorschriften)
          oder sich auch aus vertraglichen Regelungen (z.B. Angaben zum
          Vertragspartner) ergeben kann.
          <br /> Mitunter kann es zu einem Vertragsschluss erforderlich sein,
          dass eine betroffene Person uns personenbezogene Daten zur Verfügung
          stellt, die in der Folge durch uns verarbeitet werden müssen. Die
          betroffene Person ist beispielsweise verpflichtet uns personenbezogene
          Daten bereitzustellen, wenn unser Unternehmen mit ihr einen Vertrag
          abschließt. Eine Nichtbereitstellung der personenbezogenen Daten hätte
          zur Folge, dass der Vertrag mit dem Betroffenen nicht geschlossen
          werden könnte.
          <br /> Vor einer Bereitstellung personenbezogener Daten durch den
          Betroffenen muss sich der Betroffene an einen unserer Mitarbeiter
          wenden. Unser Mitarbeiter klärt den Betroffenen einzelfallbezogen
          darüber auf, ob die Bereitstellung der personenbezogenen Daten
          gesetzlich oder vertraglich vorgeschrieben oder für den
          Vertragsabschluss erforderlich ist, ob eine Verpflichtung besteht, die
          personenbezogenen Daten bereitzustellen, und welche Folgen die
          Nichtbereitstellung der personenbezogenen Daten hätte.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          15. Bestehen einer automatisierten Entscheidungsfindung
        </h2>
        <p>
          Als verantwortungsbewusstes Unternehmen verzichten wir auf eine
          automatische Entscheidungsfindung oder ein Profiling.
          <br />
          Diese Datenschutzerklärung wurde durch den
          Datenschutzerklärungs-Generator DSGVO der Deutschen Gesellschaft für
          Datenschutz erstellt.
        </p>
      </div>
    );
  } else {
    return (
      <div className="min-h-full px-5 text-foreground gap-4 flex flex-col">
        <h1 className="font-semibold text-foreground/90 text-2xl leading-[28px] tracking-[-0.015em] text-center mb-6">
          Privacy Policy
        </h1>

        <p>
          We are very pleased about your interest in our company. Data
          protection is of particular importance to {companyName}
          (hereinafter referred to as "data controller"). The use of the
          internet pages of the data controller is generally possible without
          providing any personal data. However, if a data subject wishes to make
          use of special services of our company via our website, it may be
          necessary to process personal data. If the processing of personal data
          is necessary and there is no legal basis for such processing, we
          generally obtain the consent of the data subject.
        </p>
        <p>
          The processing of personal data, such as the name, address, e-mail
          address or telephone number of a data subject, is always carried out
          in accordance with the General Data Protection Regulation and in
          accordance with the country-specific data protection provisions
          applicable to the data controller. By means of this data protection
          declaration, our company would like to inform the public about the
          type, scope and purpose of the personal data collected, used and
          processed by us. Furthermore, data subjects are informed of their
          rights by means of this data protection declaration.
        </p>
        <p>
          As the controller, the data controller has implemented numerous
          technical and organizational measures to ensure the most complete
          protection of personal data processed through this website.
          Nevertheless, internet-based data transmissions can in principle have
          security gaps, so that absolute protection cannot be guaranteed.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">1. Definitions</h2>
        <p>
          The data protection declaration of {companyName} is based on the terms
          used by the European Directive and Regulation Giver in the adoption of
          the General Data Protection Regulation (GDPR). Our data protection
          declaration should be easy to read and understand for the public as
          well as for our customers and business partners. To ensure this, we
          would like to explain the terms used in advance.
        </p>
        <p>
          We use the following terms, among others, in this data protection
          declaration:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <strong>personal data:</strong> Personal data is any information
            relating to an identified or identifiable natural person
            (hereinafter referred to as "data subject"). A natural person is
            considered identifiable if they can be identified directly or
            indirectly, in particular by means of assignment to an identifier
            such as a name, an identification number, location data, an online
            identifier or one or more special characteristics that express the
            physical, physiological, genetic, psychological, economic, cultural
            or social identity of this natural person.
          </li>
          <li>
            <strong>data subject:</strong> Data subject is any identified or
            identifiable natural person whose personal data is processed by the
            controller.
          </li>
          <li>
            <strong>processing:</strong> Processing is any operation or series
            of operations carried out with or without the aid of automated
            procedures in connection with personal data, such as the collection,
            recording, organization, ordering, storage, adaptation or
            modification, reading, retrieval, use, disclosure by transmission,
            dissemination or any other form of provision, comparison or linking,
            restriction, deletion or destruction.
          </li>
          <li>
            <strong>restriction of processing:</strong> Restriction of
            processing is the marking of stored personal data with the aim of
            restricting its future processing.
          </li>
          <li>
            <strong>profiling:</strong> Profiling is any type of automated
            processing of personal data that consists in using this personal
            data to evaluate certain personal aspects relating to a natural
            person, in particular to analyze or predict aspects relating to work
            performance, economic situation, health, personal preferences,
            interests, reliability, behavior, location or relocation of this
            natural person.
          </li>
          <li>
            <strong>pseudonymization:</strong> Pseudonymization is the
            processing of personal data in a way in which the personal data can
            no longer be assigned to a specific data subject without the use of
            additional information, provided that this additional information is
            kept separately and is subject to technical and organizational
            measures that ensure that the personal data is not assigned to an
            identified or identifiable natural person.
          </li>
          <li>
            <strong>Controller or controller for the processing:</strong>{" "}
            Controller or controller for the processing is the natural or legal
            person, authority, institution or other body that alone or jointly
            with others decides on the purposes and means of processing personal
            data. If the purposes and means of this processing are specified by
            Union law or the law of the member states, the controller or the
            specific criteria for their designation can be provided for under
            Union law or the law of the member states.
          </li>
          <li>
            <strong>processor:</strong> Processor is a natural or legal person,
            authority, institution or other body that processes personal data on
            behalf of the controller.
          </li>
          <li>
            <strong>recipient:</strong> Recipient is a natural or legal person,
            authority, institution or other body to whom personal data is
            disclosed, regardless of whether it is a third party or not.
            However, authorities that may receive personal data as part of a
            specific investigation mandate under Union law or the law of the
            member states are not considered recipients.
          </li>
          <li>
            <strong>third party:</strong> Third party is a natural or legal
            person, authority, institution or other body other than the data
            subject, the controller, the processor and the persons who are
            authorized to process the personal data under the direct
            responsibility of the controller or the processor.
          </li>
          <li>
            <strong>consent:</strong> Consent is any expression of will given
            voluntarily for the specific case in an informed manner and
            unequivocally by the data subject in the form of a declaration or
            other clear affirmative action with which the data subject indicates
            that they consent to the processing of their personal data.
          </li>
        </ul>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          2. Name and address of the controller
        </h2>
        <p>
          The controller within the meaning of the General Data Protection
          Regulation, other data protection laws in force in the member states
          of the European Union and other provisions with a data protection
          character is:
        </p>
        <address className="not-italic">{address}</address>
        <p>
          If you have any questions about data protection, please contact our
          data protection officer:
          <br />
          <br />
          {privacyOfficer}
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          3. Collection of general data and information
        </h2>
        <p>
          The website of {companyName} collects a series of general data and
          information each time the website is accessed by a data subject or an
          automated system. This general data and information is stored in the
          log files of the server. The following can be recorded:
        </p>
        <ul className="list-disc pl-6">
          <li>the browser types and versions used</li>
          <li>the operating system used by the accessing system</li>
          <li>
            the website from which an accessing system reaches our website
            (so-called referrer)
          </li>
          <li>
            the sub-web pages that are accessed via an accessing system on our
            website
          </li>
          <li>the date and time of access to the website</li>
          <li>an internet protocol address (IP address)</li>
          <li>the internet service provider of the accessing system</li>
          <li>
            other similar data and information that serve to avert danger in the
            event of attacks on our information technology systems
          </li>
        </ul>
        <p>This information is required to</p>
        <ol className="list-decimal pl-6">
          <li>to deliver the content of our website correctly,</li>
          <li>
            to optimize the content of our website and the advertising for it,
          </li>
          <li>
            to ensure the long-term functionality of our information technology
            systems and the technology of our website, and
          </li>
          <li>
            to provide law enforcement authorities with the information
            necessary for prosecution in the event of a cyber attack. This
            anonymously collected data and information is therefore evaluated
            statistically by the data controller on the one hand and with the
            aim of increasing data protection and data security in our company
            in order to ultimately ensure an optimal level of protection for the
            personal data processed by us. The anonymous data of the server log
            files are stored separately from all personal data provided by a
            data subject
          </li>
        </ol>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          4. Google TAG Manager
        </h2>
        <p>
          <strong>Type and purpose of processing:</strong> We use the service
          called Google Tag Manager from Google. "Google" is a group of
          companies and consists of Google Ireland Ltd. (provider of the
          service), Gordon House, Barrow Street, Dublin 4, Ireland and Google
          LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA and other
          affiliated companies of Google LLC.
          <br />
          We have concluded a data processing agreement with Google. The Google
          Tag Manager is an auxiliary service and only processes personal data
          itself for technically necessary purposes. The Google Tag Manager
          ensures that other components are loaded, which in turn may collect
          data. The Google Tag Manager does not access this data.
          <br />
          Further information about Google Tag Manager can be found in Google's
          data protection regulations.
          <br /> Please note that American authorities may be able to access
          personal data that is inevitably exchanged with Google when this
          service is integrated due to the Internet Protocol (TCP) due to
          American laws such as the Cloud Act.
        </p>
        <p>
          <strong>Legal basis:</strong> The legal basis for the integration of
          Google and the associated data transfer to Google is your consent
          (Art. 6 Para. 1 lit. a GDPR).
        </p>
        <p>
          <strong>Recipient:</strong> Each time you visit our website, personal
          data, including your IP address, is transmitted to Google in the USA.
          This personal data is stored by Google. Google may pass on this
          personal data collected via the technical process to third parties.
          Our company does not contain any information from Google that could be
          used to identify the data subject
        </p>
        <p>
          <strong>Storage period:</strong> These cookies expire after 30 days
          and are not used for personal identification.
        </p>
        <p>
          <strong>Third country transfer:</strong> Google processes your data in
          the USA and has submitted to the EU_US Privacy Shield{" "}
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://www.privacyshield.gov/EU-US-Framework"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.privacyshield.gov/EU-US-Framework
          </a>
          .
        </p>
        <p>
          <strong>Revocation of consent:</strong> If you do not want to
          participate in tracking, you can refuse to set a cookie for this
          purpose - for example, via a browser setting that generally
          deactivates the automatic setting of cookies or set your browser so
          that cookies from the "googleleadservices.com" domain are blocked.
          <br />
          Please note that you must not delete the opt-out cookies as long as
          you do not want measurement data to be recorded. If you have deleted
          all your cookies in the browser, you must set the respective opt-out
          cookie again.
        </p>
        <p>
          <strong>Provision required or required:</strong> The provision of your
          personal data is voluntary, based solely on your consent. If you
          prevent access, this may lead to functional restrictions on the
          website.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          5. Use of Amazon Web Services (AWS)
        </h2>
        <p>
          <strong>Type and purpose of processing:</strong> The provider is
          Amazon Web Services EMEA SARL, 38 Avenue John F. Kennedy, 1855
          Luxembourg (hereinafter AWS).
          <br />
          When you use our products, your personal data is processed on AWS's
          servers. Personal data may also be transmitted to AWS's parent company
          in the USA. The data transfer to the USA is based on the EU standard
          contractual clauses. Details can be found here:
          <br />
          <a
            className="underline underline-offset-2 hover:decoration-primary break-all"
            href="https://aws.amazon.com/de/blogs/security/aws-gdpr-data-processing-addendum/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://aws.amazon.com/de/blogs/security/aws-gdpr-data-processing-addendum/
          </a>
          <br />
          Further information can be found in AWS's data protection declaration:
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://aws.amazon.com/de/privacy/?nc1=f_pr"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://aws.amazon.com/de/privacy/?nc1=f_pr
          </a>
        </p>
        <p>
          <strong>Legal basis:</strong> AWS is used on the basis of Art. 6 Para.
          1 lit. f GDPR. If corresponding consent has been requested, processing
          is carried out exclusively on the basis of Art. 6 Para. 1 lit. a GDPR
          and Section 25 Para. 1 TTDSG, insofar as the consent includes the
          storage of cookies or access to information in the user's end device
          (e.g. device fingerprinting) within the meaning of the TTDSG. Consent
          can be revoked at any time.
        </p>
        <p>
          <strong>Third country transfer:</strong> The company has a
          certification according to the “EU-US Data Privacy Framework” (DPF).
          The DPF is an agreement between the European Union and the USA that is
          intended to ensure compliance with European data protection standards
          for data processing in the USA. Every company certified according to
          the DPF undertakes to comply with these data protection standards.
          Further information on this can be obtained from the provider at the
          following link:
          <br />
          <a
            className="underline underline-offset-2 hover:decoration-primary break-all"
            href="https://www.dataprivacyframework.gov/s/participant-search/participant-detail?contact=true&id=a2zt0000000TOWQAA4&status=Active"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.dataprivacyframework.gov/s/participant-search/participant-detail?contact=true&id=a2zt0000000TOWQAA4&status=Active
          </a>
        </p>
        <p>
          <strong>Order processing:</strong> We have concluded a contract for
          order processing (AVV) for the use of the above-mentioned service.
          This is a contract required by data protection law that ensures that
          this processes the personal data of our website visitors only in
          accordance with our instructions and in compliance with the GDPR.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          6. Registration in our WebApp
        </h2>
        <p>
          The data subject has the option of registering in the data
          controller's WebApp, providing personal data. The personal data
          transmitted to the data controller is determined by the respective
          input mask that is used for registration. The personal data entered by
          the data subject is collected and stored exclusively for internal use
          by the data controller and for its own purposes. The data controller
          may arrange for the transfer to one or more processors, such as a
          parcel service provider, who also uses the personal data exclusively
          for internal use that is attributable to the data controller. By
          registering in the data controller's WebApp, the IP address assigned
          by the Internet service provider (ISP) of the data subject, the date
          and time of registration are also stored. This data is stored against
          the background that this is the only way to prevent misuse of our
          services and this data enables committed crimes to be investigated if
          necessary. In this respect, the storage of this data is necessary to
          protect the data controller. This data will not be passed on to third
          parties unless there is a legal obligation to pass it on or the
          transfer serves criminal prosecution.
          <br />
          The registration of the data subject with the voluntary provision of
          personal data serves the data controller to offer the data subject
          content or services that, due to the nature of the matter, can only be
          offered to registered users. Registered persons are free to change the
          personal data provided during registration at any time or to have it
          completely deleted from the data stock of the data controller.
          <br />
          The data controller provides each data subject with information about
          which personal data about the data subject is stored at any time upon
          request. Furthermore, the data controller corrects or deletes personal
          data at the request or notice of the data subject, provided that there
          are no legal storage obligations to the contrary. The entirety of the
          data controller's employees are available to the data subject as
          contact persons in this context.
        </p>
        <h2 className="font-semibold text-lg mt-6 mb-2">
          7. Contact option via the WebApp
        </h2>
        <p>
          Due to legal regulations, the WebApp contains information that enables
          quick electronic contact with our company and direct communication
          with us, which also includes a general address for so-called
          electronic mail (e-mail address). If a data subject contacts the
          controller using the
          <br />
          contact form, the personal data transmitted by the data subject is
          automatically saved. Such personal data voluntarily transmitted by a
          data subject to the controller is stored for the purpose of processing
          or contacting the data subject. This personal data will not be passed
          on to third parties.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          8. Routine deletion and blocking of personal data
        </h2>
        <p>
          The controller processes and stores personal data of the data subject
          only for the period of time that is necessary to achieve the purpose
          of storage or if this has been provided for by the European Directive
          and Regulation Giver or another legislator in laws or regulations to
          which the controller is subject.
          <br />
          If the storage purpose no longer applies or if a storage period
          prescribed by the European Directive and Regulation Giver or another
          competent legislator expires, the personal data is routinely blocked
          or deleted in accordance with the statutory provisions.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          9. Payment method: Data protection regulations for Stripe as a payment
          method
        </h2>
        <p>
          We offer the option of processing the payment process via the payment
          service provider Stripe, ℅ Legal Process, 510,Townsend St., San
          Francisco, CA 94103 (Stripe). This corresponds to our legitimate
          interest in offering an efficient and secure payment method (Art. 6
          Para. 1 lit. f GDPR). In this context, we pass on the following data
          to Stripe, insofar as this is necessary for the fulfillment of the
          contract (Art. 6 Para. 1 lit b. GDPR).
        </p>
        <ul className="list-disc pl-6">
          <li>Name of cardholder</li>
          <li>E-mail address</li>
          <li>Customer number</li>
          <li>Order number</li>
          <li>Bank details</li>
          <li>Credit card details</li>
          <li>Credit card validity period</li>
          <li>Credit card verification number (CVC)</li>
          <li>Date and time of transaction</li>
          <li>Transaction amount</li>
          <li>Name of provider</li>
          <li>Location</li>
        </ul>
        <p>
          The processing of the data specified in this section is neither
          required by law nor by contract. Without the transmission of your
          personal data, we cannot carry out a payment via Stripe.
          <br />
          Stripe has a dual role as controller and processor in data processing
          activities. As a controller, Stripe uses your transmitted data to
          fulfill regulatory obligations. This corresponds to Stripe's
          legitimate interest (in accordance with Art. 6 Para. 1 lit. f GDPR)
          and serves to implement the contract (in accordance with Art. 6 Para.
          1 lit. b GDPR). We have no influence on this process.
          <br />
          Stripe acts as a processor in order to be able to complete
          transactions within the payment networks. Within the framework of the
          order processing relationship, Stripe acts exclusively in accordance
          with our instructions and has been contractually obliged to comply
          with data protection regulations within the meaning of Art. 28 GDPR.
          <br />
          Stripe has implemented compliance measures for international data
          transfers. These apply to all global activities in which Stripe
          processes personal data of natural persons in the EU. These measures
          are based on the EU standard contractual clauses (SCCs).
          <br />
          Further information on objection and removal options vis-à-vis Stripe
          can be found at:
          <a
            className="underline underline-offset-2 hover:decoration-primary"
            href="https://stripe.com/privacy-center/legal"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://stripe.com/privacy-center/legal
          </a>
          <br />
          Your data will be stored by us until the payment process is completed.
          This also includes the period required for processing refunds, claims
          management and fraud prevention. The legal retention period applies.
          <br />
          <br />
          The European operating company is Stripe Payments Europe Limited 1
          Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210, Ireland
          Attention: Stripe Legal
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          10. Rights of the data subject
        </h2>
        <ul className="list-disc pl-6">
          <li>
            <p>
              <strong>Right to confirmation:</strong> Every data subject has the
              right granted by the European Directive and Regulation Giver to
              request confirmation from the controller as to whether personal
              data concerning them is being processed. If a data subject wishes
              to exercise this right to confirmation, they may contact an
              employee of the controller at any time.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to information:</strong> Any person affected by the
              processing of personal data has the right granted by the European
              Directive and Regulation Giver to obtain, at any time and free of
              charge, information from the controller about the personal data
              stored about them and a copy of this information. Furthermore, the
              European Directive and Regulation Giver has granted the data
              subject access to the following information:
            </p>
            <ul className="list-disc pl-6">
              <li>the processing purposes</li>
              <li>the categories of personal data being processed</li>

              <li>
                the recipients or categories of recipients to whom the personal
                data have been or will be disclosed, in particular to recipients
                in third countries or to international organizations
              </li>

              <li>
                if possible, the planned duration for which the personal data
                will be stored or, if this is not possible, the criteria for
                determining this duration
              </li>

              <li>
                the existence of a right to rectification or erasure of personal
                data concerning them or to restriction of processing by the
                controller or a right to object to such processing
              </li>

              <li>
                the existence of a right of appeal to a supervisory authority if
                the personal data are not collected from the data subject:
              </li>

              <li>All available information about the origin of the data</li>

              <li>
                the existence of automated decision-making, including profiling
                in accordance with Article 22(1) and (4) GDPR and — at least in
                these cases — meaningful information about the logic involved
                and the scope and intended effects of such processing for the
                data subject
              </li>
            </ul>
            <p>
              Furthermore, the data subject has the right to information as to
              whether personal data has been transferred to a third country or
              to an international organization. If this is the case, the data
              subject also has the right to obtain information about the
              appropriate guarantees in connection with the transfer.
              <br />
              If a data subject wishes to exercise this right to information,
              they may contact an employee of the controller at any time.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to rectification:</strong> Any person affected by
              the processing of personal data has the right granted by the
              European Directive and Regulation Giver to request the immediate
              correction of inaccurate personal data concerning them.
              Furthermore, the data subject has the right to request the
              completion of incomplete personal data, including by means of a
              supplementary declaration, taking into account the purposes of the
              processing.
              <br />
              If a data subject wishes to exercise this right to rectification,
              they may contact an employee of the controller at any time.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to erasure (right to be forgotten):</strong> Any
              person affected by the processing of personal data has the right
              granted by the European Directive and Regulation Giver to request
              the controller to erase personal data concerning them immediately,
              provided that one of the following reasons applies and insofar as
              the processing is not necessary:
              <br />
              The personal data were collected or otherwise processed for
              purposes for which they are no longer necessary. The data subject
              withdraws their consent on which the processing was based pursuant
              to Art. 6 Para. 1 letter a GDPR or Art. 9 Para. 2 letter a GDPR,
              and there is no other legal basis for the processing. The data
              subject objects to the processing pursuant to Art. 21 Para. 1 GDPR
              and there are no overriding legitimate grounds for the processing,
              or the data subject objects to the processing pursuant to Art. 21
              Para. 2 GDPR. The personal data have been processed unlawfully.
              The erasure of personal data is necessary to fulfill a legal
              obligation under Union law or the law of the member states to
              which the controller is subject. The personal data were collected
              in relation to services offered by the information society
              pursuant to Art. 8 Para. 1 GDPR. If one of the above-mentioned
              reasons applies and a data subject wishes to initiate the erasure
              of personal data stored by the controller, they may contact an
              employee of the controller at any time. The employee of the
              controller will arrange for the erasure request to be complied
              with immediately.
              <br />
              If the personal data have been made public by the controller and
              our company as controller is obliged to erase the personal data
              pursuant to Art. 17 Para. 1 GDPR, the controller shall take
              appropriate measures, including technical measures, taking into
              account the available technology and the implementation costs, to
              inform other data controllers who process the published personal
              data that the data subject has requested these other data
              controllers to erase all links to this personal data or copies or
              replications of this personal data, insofar as the processing is
              not necessary. The employee of the controller will arrange what is
              necessary in individual cases.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to restriction of processing:</strong> Any person
              affected by the processing of personal data has the right granted
              by the European Directive and Regulation Giver to request the
              controller to restrict processing if one of the following
              conditions is met:
              <br />
              The accuracy of the personal data is disputed by the data subject,
              for a period that allows the controller to verify the accuracy of
              the personal data. The processing is unlawful, the data subject
              objects to the erasure of the personal data and instead requests
              the restriction of the use of the personal data. The controller no
              longer needs the personal data for the purposes of processing, but
              the data subject needs them to assert, exercise or defend legal
              claims. The data subject has objected to the processing pursuant
              to Art. 21 Para. 1 GDPR and it is not yet clear whether the
              legitimate reasons of the controller outweigh those of the data
              subject. If one of the above-mentioned conditions is met and a
              data subject wishes to request the restriction of personal data
              stored by the controller, they may contact an employee of the
              controller at any time. The employee of the controller will
              arrange for the restriction of processing.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to data portability:</strong> Any person affected by
              the processing of personal data has the right granted by the
              European Directive and Regulation Giver to receive the personal
              data concerning them that has been provided to a controller by the
              data subject in a structured, commonly used and machine-readable
              format. They also have the right to transfer this data to another
              controller without hindrance from the controller to whom the
              personal data was provided, provided that the processing is based
              on consent pursuant to Art. 6 Para. 1 letter a GDPR or Art. 9
              Para. 2 letter a GDPR or on a contract pursuant to Art. 6 Para. 1
              letter b GDPR and the processing is carried out using automated
              procedures, unless the processing is necessary for the performance
              of a task that is in the public interest or is carried out in the
              exercise of official authority vested in the controller.
              <br />
              Furthermore, when exercising their right to data portability
              pursuant to Art. 20 Para. 1 GDPR, the data subject has the right
              to have the personal data transferred directly from one controller
              to another, where this is technically feasible and provided that
              this does not affect the rights and freedoms of other persons.
              <br />
              To assert the right to data portability, the data subject may
              contact an employee of the controller at any time.
            </p>
          </li>
          <li>
            <p>
              <strong>Right to object:</strong> Any person affected by the
              processing of personal data has the right granted by the European
              Directive and Regulation Giver to object at any time, on grounds
              arising from their particular situation, to the processing of
              personal data concerning them that is carried out on the basis of
              Art. 6 Para. 1 letters e or f GDPR. This also applies to profiling
              based on these provisions. <br />
              In the event of an objection, the controller will no longer
              process the personal data unless we can demonstrate compelling
              legitimate grounds for the processing that outweigh the interests,
              rights and freedoms of the data subject, or the processing serves
              to assert, exercise or defend legal claims.
              <br />
              If the controller processes personal data for the purpose of
              direct advertising, the data subject has the right to object at
              any time to the processing of personal data for the purpose of
              such advertising. This also applies to profiling insofar as it is
              associated with such direct advertising. If the data subject
              objects to the controller processing data for direct marketing
              purposes, the controller will no longer process the personal data
              for these purposes.
              <br />
              In addition, the data subject has the right to object, on grounds
              arising from their particular situation, to the processing of
              personal data concerning them that is carried out by the
              controller for scientific or historical research purposes or for
              statistical purposes pursuant to Art. 89 Para. 1 GDPR, unless such
              processing is necessary for the performance of a task carried out
              in the public interest.
              <br />
              To exercise the right to object, the data subject may contact any
              employee of the controller directly or another employee. The data
              subject is also free to exercise their right to object by means of
              automated procedures using technical specifications in connection
              with the use of information society services, notwithstanding
              Directive 2002/58/EC.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Automated individual decisions including profiling
              </strong>{" "}
              Any person affected by the processing of personal data has the
              right granted by the European Directive and Regulation Giver not
              to be subject to a decision based exclusively on automated
              processing, including profiling, that has legal effect on them or
              significantly impairs them in a similar manner, provided that the
              decision (1) is not necessary for the conclusion or performance of
              a contract between the data subject and the controller, or (2) is
              admissible under Union or member state law to which the controller
              is subject and these laws contain appropriate measures to
              safeguard the rights and freedoms and the legitimate interests of
              the data subject or (3) is based on the express consent of the
              data subject.
              <br />
              If the decision (1) is necessary for the conclusion or performance
              of a contract between the data subject and the controller or (2)
              is based on the express consent of the data subject, the
              controller shall take appropriate measures to safeguard the rights
              and freedoms and the legitimate interests of the data subject,
              which shall include at least the right to obtain the intervention
              of a person on the part of the controller, to present their own
              point of view and to contest the decision.
              <br />
              If the data subject wishes to assert rights relating to automated
              decisions, they may contact an employee of the controller at any
              time.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Right to withdraw consent under data protection law
              </strong>{" "}
              Any person affected by the processing of personal data has the
              right granted by the European Directive and Regulation Giver to
              withdraw consent to the processing of personal data at any time.
              <br />
              If the data subject wishes to exercise their right to withdraw
              consent, they may contact an employee of the controller at any
              time.
            </p>
          </li>
        </ul>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          11. Legal basis of processing
        </h2>
        <p>
          Art. 6 I lit. a GDPR serves our company as the legal basis for
          processing operations in which we obtain consent for a specific
          processing purpose. If the processing of personal data is necessary
          for the performance of a contract to which the data subject is a
          party, as is the case, for example, in processing operations that are
          necessary for the delivery of goods or the provision of other services
          or consideration, the processing is based on Art. 6 I lit. b GDPR. The
          same applies to such processing operations that are necessary to carry
          out pre-contractual measures, for example in cases of inquiries about
          our products or services. If our company is subject to a legal
          obligation which requires the processing of personal data, such as to
          fulfill tax obligations, the processing is based on Art. 6 I lit. c
          GDPR. In rare cases, the processing of personal data may be necessary
          to protect the vital interests of the data subject or another natural
          person. This would be the case, for example, if a visitor were injured
          on our premises and his name, age, health insurance details or other
          vital information would have to be passed on to a doctor, hospital or
          other third party. Then the processing would be based on Art. 6 I lit.
          d GDPR. Ultimately, processing operations could be based on Art. 6 I
          lit. f GDPR. Processing operations that are not covered by any of the
          aforementioned legal bases are based on this legal basis if the
          processing is necessary to protect a legitimate interest of our
          company or a third party, provided that the interests, fundamental
          rights and freedoms of the data subject do not outweigh them. Such
          processing operations are permitted to us in particular because they
          are specifically mentioned by the European legislator
          <br />
          were. In that regard, it took the view that a legitimate interest
          could be assumed if the data subject is a customer of the controller
          (Recital 47 sentence 2 GDPR).
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          12. Legitimate interests in the processing pursued by the controller
          or a third party
        </h2>
        <p>
          If the processing of personal data is based on Article 6 I lit. f
          GDPR, our legitimate interest is the conduct of our business for the
          benefit of the well-being of all our employees and our shareholders.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          13. Duration for which the personal data is stored
        </h2>
        <p>
          The criterion for the duration of the storage of personal data is the
          respective statutory retention period. After the deadline, the
          corresponding data will be routinely deleted, provided they are no
          longer required to fulfill the contract or initiate a contract.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          14. Legal or contractual provisions for the provision of personal
          data; Necessity for the conclusion of the contract; Obligation of the
          data subject to provide the personal data; Possible consequences of
          non-provision
        </h2>
        <p>
          We would like to inform you that the provision of personal data is
          sometimes required by law (e.g. tax regulations) or may also result
          from contractual provisions (e.g. information on the contractual
          partner).
          <br />
          Sometimes it may be necessary for a contract to be concluded if a data
          subject provides us with personal data that we then have to process.
          The data subject is obliged, for example, to provide us with personal
          data if our company concludes a contract with them. Failure to provide
          personal data would mean that the contract with the data subject could
          not be concluded.
          <br />
          Before the data subject provides personal data, the data subject must
          contact one of our employees. Our employee will inform the data
          subject on a case-by-case basis whether the provision of personal data
          is required by law or contract or is necessary for the conclusion of
          the contract, whether there is an obligation to provide the personal
          data and what consequences the failure to provide the personal data
          would have.
        </p>

        <h2 className="font-semibold text-lg mt-6 mb-2">
          15. Existence of automated decision-making
        </h2>
        <p>
          As a responsible company, we do not use automatic decision-making or
          profiling.
          <br />
          This data protection declaration was created using the data protection
          declaration generator of the German Society for Data Protection.
        </p>
      </div>
    );
  }
};

export default DefaultDataPrivacy;
